import React, { useEffect, useState } from "react";

import HttpManager from "../../services/HttpManager";
import { ArticleDTO } from "../../models/ArticleDTO";
import { SerialPicker } from "../../navigation/v2/store/warehouses/traslados";
import { DeliveryPlan } from "../../services/v2/inventory.service";
import { kitDeliveryStatus } from "../../constants";

export type DeliveryAction =
	| { kind: "delivery"; type: "partial" | "total" }
	| { kind: "status"; deliveryStatusId: number }
	| { kind: "note" }
	| { kind: "items"; items: { componentId: number | null; articleId: number; quantity: number }[] };

export interface DeliveryPromptProps {
	kit: ArticleDTO;
	campusId: number;
	busy?: boolean;
	error?: string | null;
	onConfirm: (action: DeliveryAction, note: string, warehouseId?: number | null) => void;
	onCancel: () => void;
}

/**
 * "Registrar Entrega". Cuando el producto ya está en el catálogo nuevo, se entrega pieza por pieza:
 * se marca qué salió, con qué talla o sustituto, y el inventario se descuenta al guardar.
 * Los kits vendidos con el catálogo viejo mantienen la entrega simple (parcial / completa / nota).
 */
const DeliveryPrompt: React.FC<DeliveryPromptProps> = ({ kit, campusId, busy, error, onConfirm, onCancel }) => {
	const unknown = kit.deliveryStatusId === kitDeliveryStatus.UNKNOWN;
	/** Esteban, 2026-09-30: nada se entrega (ni parcial) hasta que está pagado completo; la nota sí se puede dejar. */
	const paidInFull = kit.statusId === 2 || kit.statusId === 3;
	const [plan, setPlan] = useState<DeliveryPlan | null>(null);
	const [loadingPlan, setLoadingPlan] = useState<boolean>(true);
	const [choice, setChoice] = useState<string>(!paidInFull ? "note" : unknown ? "" : "partial");
	const [note, setNote] = useState<string>("");
	/** articleId elegido y cantidad, por línea del kit. */
	const [picked, setPicked] = useState<Record<string, { articleId: number; quantity: number; serials?: string[] }>>({});
	/** De qué bodega sale. Online no tiene bodega propia: elige entre las de su país. */
	const [warehouseId, setWarehouseId] = useState<number | null>(null);

	useEffect(() => {
		let alive = true;
		setLoadingPlan(true);
		HttpManager.getInstance().inventoryService.deliveryPlan(campusId, kit.id, warehouseId)
			.then((response) => {
				if (!alive) return;
				setPlan(response.data);
				if (warehouseId === null && response.data.warehouse) setWarehouseId(response.data.warehouse.id);
				setPicked({});
			})
			.catch(() => { if (alive) setPlan(null); })
			.finally(() => { if (alive) setLoadingPlan(false); });
		return () => { alive = false; };
	}, [campusId, kit.id, warehouseId]);

	useEffect(() => {
		const onKey = (event: KeyboardEvent) => { if (event.key === "Escape" && !busy) onCancel(); };
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [busy, onCancel]);

	const byPieces = !!plan && plan.tracksInventory !== false && plan.lines.length > 0;
	const pendingLines = byPieces ? plan!.lines.filter((line) => line.pending > 0) : [];
	const trimmed = note.trim();
	const chosen = Object.keys(picked).filter((key) => picked[key] && picked[key].quantity > 0);
	const validPieces = chosen.length > 0;
	const validSimple = choice !== "" && trimmed.length >= 3;

	const key = (line: any) => String(line.componentId ?? "");
	const setPick = (line: any, articleId: number | null, quantity: number) => {
		const next = { ...picked };
		if (articleId === null || quantity < 1) delete next[key(line)];
		else next[key(line)] = { articleId, quantity };
		setPicked(next);
	};

	const confirm = () => {
		if (byPieces) {
			if (!validPieces) return;
			onConfirm({
				kind: "items",
				items: chosen.map((componentKey) => ({
					componentId: componentKey === "" ? null : Number(componentKey),
					articleId: picked[componentKey].articleId,
					quantity: picked[componentKey].quantity,
					serials: picked[componentKey].serials || []
				}))
			}, trimmed, warehouseId);
			return;
		}
		if (!validSimple) return;
		if (choice === "partial" || choice === "total") onConfirm({ kind: "delivery", type: choice }, trimmed);
		else if (choice === "was_delivered") onConfirm({ kind: "status", deliveryStatusId: kitDeliveryStatus.DELIVERED }, trimmed);
		else if (choice === "note") onConfirm({ kind: "note" }, trimmed);
	};

	const option = (value: string, title: string, hint: string) => {
		const selected = choice === value;
		return <div key={value} role="radio" aria-checked={selected} onClick={() => { if (!busy) setChoice(value); }}
			style={{ display: "flex", gap: "10px", alignItems: "flex-start", padding: "8px 10px", border: "1px solid " + (selected ? "var(--wa-acento)" : "var(--wa-filo)"), borderRadius: "8px", marginBottom: "6px", cursor: busy ? "default" : "pointer", background: selected ? "var(--wa-acento-suave)" : "var(--wa-superficie)" }}>
			<span style={{ flex: "0 0 16px", width: "16px", height: "16px", marginTop: "2px", borderRadius: "50%", border: "2px solid " + (selected ? "var(--wa-acento)" : "var(--wa-filo-3)"), background: selected ? "var(--wa-acento)" : "var(--wa-superficie)", boxShadow: selected ? "inset 0 0 0 3px var(--wa-superficie)" : "none" }} />
			<span style={{ flex: 1 }}>
				<span style={{ display: "block", fontWeight: 600, fontSize: "14px", color: "var(--wa-tinta)" }}>{title}</span>
				<span style={{ display: "block", fontSize: "12px", color: "var(--wa-apagado)" }}>{hint}</span>
			</span>
		</div>;
	};

	const enrollment = (kit.enrollments || [])[0];

	return <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
		onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onCancel(); }}
	>
		<div className="card" style={{ width: "100%", maxWidth: byPieces ? "680px" : "560px", maxHeight: "92vh", overflowY: "auto", boxShadow: "0 10px 40px rgba(0,0,0,0.25)" }} role="dialog" aria-modal="true">
			<div className="card-body">
				<h4 style={{ marginBottom: "8px" }}>Registrar Entrega</h4>
				<div style={{ background: "var(--wa-papel-2)", borderRadius: "8px", padding: "10px 12px", marginBottom: "12px", fontSize: "13px", lineHeight: 1.5 }}>
					<div style={{ fontSize: "15px", fontWeight: 700, color: "var(--wa-tinta)" }}>{kit.name}</div>
					<div style={{ color: "var(--wa-apagado)", fontSize: "11px" }}>SKU {kit.sku}{kit.quantity > 1 ? " · " + kit.quantity + " unidades" : ""}</div>
					{kit.studentName ? <div style={{ marginTop: "4px" }}><b>{kit.studentName}</b>{kit.studentCode ? <span style={{ color: "var(--wa-apagado)" }}> · {kit.studentCode}</span> : null}</div> : null}
					{enrollment ? <div style={{ color: "var(--wa-apagado)" }}>{enrollment.courseBrief || enrollment.module || enrollment.level}{enrollment.period ? " · " + enrollment.period : ""}{enrollment.schedule ? " · " + enrollment.schedule : ""}{enrollment.mentor ? " · " + enrollment.mentor : ""}</div> : null}
					<div style={{ marginTop: "4px" }}>
						Pago: <b>{kit.paidAmount.moneyFormat}</b> de {kit.subTotal.moneyFormat}
						{kit.isDebt ? <span style={{ color: "var(--wa-oro)" }}> · debe {kit.pendingAmount.moneyFormat}</span> : null}

					</div>
				</div>

				{loadingPlan ? <div style={{ padding: "16px", textAlign: "center", color: "var(--wa-apagado)" }}>Cargando lo que falta entregar…</div>
					: byPieces ? <>
						{(plan!.warehouseOptions || []).length > 1 ? <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "10px", flexWrap: "wrap" }}>
							<span style={{ fontSize: "13px", fontWeight: 600 }}>Sale de</span>
							<select className="form-select" style={{ height: "32px", fontSize: "13px", width: "auto", flex: "1 1 220px" }} disabled={busy}
								value={warehouseId === null ? "" : String(warehouseId)}
								onChange={(event) => setWarehouseId(Number(event.target.value))}>
								{(plan!.warehouseOptions || []).map((warehouse) => <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>)}
							</select>
						</div> : null}
						{plan!.blockedReason ? <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--wa-oro)", background: "var(--wa-oro-suave)", borderRadius: "8px", padding: "8px 10px", marginBottom: "10px" }}>
							{plan!.blockedReason}
						</div> : null}
						<div style={{ fontWeight: 600, fontSize: "13px", marginBottom: "6px" }}>Marca lo que estás entregando</div>
						{pendingLines.length < 1 ? <div style={{ fontSize: "13px", color: "var(--wa-verde)", padding: "8px 0" }}>Ya se entregó todo lo que compone este kit.</div>
							: pendingLines.map((line) => {
								const current = picked[key(line)];
								return <div key={key(line)} style={{ border: "1px solid var(--wa-filo)", borderRadius: "8px", padding: "8px 10px", marginBottom: "8px" }}>
									<div style={{ display: "flex", justifyContent: "space-between", gap: "8px", flexWrap: "wrap" }}>
										<span style={{ fontWeight: 600, fontSize: "14px" }}>{line.name}</span>
										<span style={{ fontSize: "12px", color: "var(--wa-apagado)" }}>faltan {line.pending} de {line.needed}{line.optional ? " · opcional" : ""}</span>
									</div>
									<div style={{ display: "flex", gap: "8px", alignItems: "center", marginTop: "6px", flexWrap: "wrap" }}>
										<select className="form-select" style={{ height: "32px", fontSize: "13px", flex: "1 1 220px" }} disabled={busy}
											value={current ? String(current.articleId) : ""}
											onChange={(event) => setPick(line, event.target.value === "" ? null : Number(event.target.value), current ? current.quantity : Math.min(1, line.pending))}>
											<option value="">No entregar ahora</option>
											{line.options.map((choiceOption) => <option key={choiceOption.articleId} value={choiceOption.articleId} disabled={choiceOption.onHand < 1}>
												{choiceOption.sizeValue ? "Talla " + choiceOption.sizeValue : choiceOption.name}{choiceOption.isSubstitute ? " (sustituto)" : ""} · hay {choiceOption.onHand}{choiceOption.inTransit ? " · " + choiceOption.inTransit + " en camino" + (choiceOption.eta ? " (" + choiceOption.eta.slice(0, 5) + ")" : "") : ""}
											</option>)}
										</select>
										{current ? <input type="number" min={1} max={line.pending} className="form-control" style={{ height: "32px", fontSize: "13px", width: "80px" }} disabled={busy}
											value={current.quantity}
											onChange={(event) => setPick(line, current.articleId, Math.max(1, Math.min(line.pending, Number(event.target.value) || 1)))} /> : null}
									</div>
									<div>
										{(() => {
											const chosenOption: any = current ? line.options.find((choiceOption: any) => choiceOption.articleId === current.articleId) : null;
											if (!chosenOption || !chosenOption.serialized) return null;
											return <SerialPicker options={chosenOption.serials || []} value={current.serials || []} max={current.quantity} busy={busy}
												onChange={(serials) => setPicked({ ...picked, [key(line)]: { ...current, serials } })} />;
										})()}
									</div>
									{line.options.every((choiceOption) => choiceOption.onHand < 1)
										? <div style={{ fontSize: "12px", color: "var(--wa-oro)", marginTop: "4px" }}>
											{(() => {
												const coming = line.options.filter((choiceOption) => (choiceOption.inTransit || 0) > 0);
												if (coming.length < 1) return "No hay existencia en esta bodega. Hay que pedirlo en Compras.";
												const units = coming.reduce((sum, choiceOption) => sum + (choiceOption.inTransit || 0), 0);
												const eta = coming.map((choiceOption) => choiceOption.eta).filter(Boolean).sort()[0];
												return "No hay existencia en esta bodega. Vienen " + units + " en camino" + (eta ? ", llegan el " + eta : "") + ".";
											})()}
										</div> : null}
								</div>;
							})}
						{plan!.deliveredItems.length ? <div style={{ marginTop: "10px" }}>
							<div style={{ fontWeight: 600, fontSize: "13px" }}>Ya entregado</div>
							{plan!.deliveredItems.map((item) => <div key={item.id} style={{ fontSize: "12px", color: "var(--wa-apagado)" }}>
								{item.createdAt} · {item.name}{item.sizeValue ? " talla " + item.sizeValue : ""} ×{item.quantity} · {item.author}
							</div>)}
						</div> : null}
					</>
					: <>
						{plan && plan.tracksInventory === false ? <div style={{ fontSize: "12px", color: "var(--wa-oro)", background: "var(--wa-oro-suave)", borderRadius: "8px", padding: "8px 10px", marginBottom: "10px" }}>
							Este producto todavía es del catálogo viejo, así que no descuenta inventario. Cuando se recree con las reglas nuevas, la entrega será pieza por pieza.
						</div> : null}
						{!paidInFull ? <>
							<div style={{ fontSize: "13px", fontWeight: 600, color: "var(--wa-oro)", background: "var(--wa-oro-suave)", borderRadius: "8px", padding: "8px 10px", marginBottom: "10px" }}>
								Se entrega cuando está pagado completo.
							</div>
							{option("note", "Agregar Nota de Entrega", "Solo deja la nota en el historial; el status no cambia.")}
						</> : unknown
							? <>
								{option("was_delivered", "Se Entregó Completo", "Queda como Entregado Completo con fecha de hoy.")}
								{option("partial", "Entrega Parcial Ahora", "Se puede registrar más de una.")}
								{option("note", "Agregar Nota de Entrega", "Solo deja la nota en el historial; el status no cambia.")}
							</>
							: <>
								{option("partial", "Entrega Parcial", "Se puede registrar más de una hasta completar.")}
								{option("total", "Se Entregó Completo", "Queda como Entregado Completo.")}
								{option("note", "Agregar Nota de Entrega", "Solo deja la nota en el historial; el status no cambia.")}
							</>}
					</>}

				<div style={{ fontWeight: 600, fontSize: "13px", marginTop: "10px", marginBottom: "4px" }}>
					{byPieces ? "Nota (opcional)" : choice === "note" ? "Nota" : "Detalle de la entrega"}
					{byPieces ? null : <span style={{ color: "var(--wa-oro)" }}> *</span>}
				</div>
				<textarea className="form-control" rows={2} disabled={busy} value={note} maxLength={2000}
					placeholder={byPieces ? "Algo que valga la pena recordar de esta entrega." : "Ej.: Libros 1 y 2 y camisa talla M. Falta el libro 3."}
					onChange={(event) => setNote(event.target.value)} style={{ resize: "vertical" }} />

				{error ? <div className="alert alert-danger" style={{ marginTop: "10px", padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}
				<div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "14px" }}>
					<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={onCancel}>Cancelar</button>
					<button type="button" className="btn btn-primary rounded-pill" disabled={busy || loadingPlan || (byPieces ? !validPieces || !!plan!.blockedReason : !validSimple)} onClick={confirm}>
						{busy ? "Guardando…" : byPieces ? "Entregar y descontar" : "Guardar"}
					</button>
				</div>
			</div>
		</div>
	</div>;
};

export default DeliveryPrompt;
